import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import localforage from "localforage";
import { RootState } from "../../app/store";
import { LoadingStatus } from "../common/commonSlice";
import {
  AddMultipleUsersReq,
  AddMultipleUsersResponse,
  changeExtraConfigAPI,
  ChangeInfoRequest,
  changeOwnInfoAPI,
  changeOwnRolesAPI,
  changeUserInfoAPI,
  changeUserRolesAPI,
  ClientTokenConfig,
  createMultipleUsersAPI,
  createRoleProfileAPI,
  createUserAPI,
  CreateUserRequest,
  deleteMultipleUserAPI,
  deleteRoleProfileAPI,
  deleteUserAPI,
  FilteredEventsResponse,
  FilteredUsersRequest,
  FilteredUsersResponse,
  getAllUsersAPI,
  getAuthConfigAPI,
  getCurrentRolesAPI,
  getExtraConfigAPI,
  getFilteredEventsAPI,
  GetFilteredEventsRequest,
  getFilteredUsersAPI,
  getOtherUserDataAPI,
  getRoleOptionsAPI,
  getRoleProfilesAPI,
  getUserDataAPI,
  PasswordData,
  PasswordDataError,
  PredeterminedRoles,
  resetOwnPasswordAPI,
  restorePasswordAPI,
  RoleProfile,
  toggleMultipleUserStatusAPI,
  toggleUserStatusAPI,
  updateAuthConfigAPI,
  updateRoleProfileAPI,
  UserData,
  UserDataErrorMessage,
} from "./configurationAPI";
import { ClientExtraConfig } from "./clients/clientAPI";

export type ConfigurationActiveComponent =
  | "Account"
  | "Users"
  | "ActionHistory"
  | "User"
  | "Clients"
  | "Client"
  | "Help"
  | "MfEditor"
  | "ModalEditor"
  | "HfEditor"
  | "Offline"
  | "None";
export type HelpActiveComponent =
  | "None"
  | "Documentacion Hyperflow"
  | "Documentacion CoreId"
  | "Documentacion MagicForms"
  | "Documentacion General"
  | "Documentacion Pagares"
  | "Documentacion Firma"
  | "Documentacion Configuracion";

export interface ConfigurationState {
  activeComponent: ConfigurationActiveComponent;
  helpActiveComponent: HelpActiveComponent;
  accountLoadingStatus: LoadingStatus;
  passwordChangeStatus: LoadingStatus;
  currentRolesLoading: LoadingStatus;
  roleOptionsLoading: LoadingStatus;
  createUserLoading: LoadingStatus;
  usersLoading: LoadingStatus;
  otherUserLoading: LoadingStatus;
  restorePasswordLoading: LoadingStatus;
  restorePasswordError?: string;
  resetOwnPasswordLoading: LoadingStatus;
  changeOwnInfoLoading: LoadingStatus;
  changeUserInfoLoading: LoadingStatus;
  changeUserInfoError?: string;
  deleteUserLoading: LoadingStatus;
  deleteUserError?: string;
  changeRolesLoading: LoadingStatus;
  changeRolesError?: string;
  eventsLoading: LoadingStatus;
  currentUserData: UserData | null;
  passwordData: PasswordData;
  userDataError: UserDataErrorMessage;
  passwordDataError: PasswordDataError;
  roleOptions: PredeterminedRoles[];
  currentRoles: string[];
  filteredRequest: FilteredUsersRequest;
  filteredEventRequest: GetFilteredEventsRequest;
  currentPage: number;
  currentNumPages: number;
  users: FilteredUsersResponse | null;
  allUsers: UserData[];
  events: FilteredEventsResponse | null;
  otherUser: UserData | null;
  generatedPassword: string | null;
  createUserError?: string;
  eventCurrentPage: number;
  eventCurrentNumPages: number;
  roleProfiles: RoleProfile[];
  roleProfileCreationLoading: LoadingStatus;
  roleProfileCreationError: string;
  createMultipleUsersResult?: AddMultipleUsersResponse;
  changeMultipleUsersLoading: LoadingStatus;
  changeMultipleUsersError: string;
  extraConfigLoading: LoadingStatus;
  extraConfig: ClientExtraConfig | null;
  tokenConfig: ClientTokenConfig | null;
  tokenConfigLoading: LoadingStatus;
}

const initialState: ConfigurationState = {
  filteredEventRequest: {
    completed: null,
    page: 0,
    finishedDate: null,
    entityId: null,
    service: null,
    startedDate: null,
    type: null,
    userId: null,
    pageSize: 10,
  },
  activeComponent: "Account",
  helpActiveComponent: "None",
  accountLoadingStatus: "idle",
  passwordChangeStatus: "idle",
  roleOptionsLoading: "idle",
  currentRolesLoading: "idle",
  changeOwnInfoLoading: "idle",
  createUserLoading: "idle",
  restorePasswordLoading: "idle",
  changeUserInfoLoading: "idle",
  usersLoading: "idle",
  resetOwnPasswordLoading: "idle",
  deleteUserLoading: "idle",
  otherUser: null,
  otherUserLoading: "idle",
  changeRolesLoading: "idle",
  eventsLoading: "idle",
  tokenConfigLoading: "idle",
  currentUserData: null,
  passwordData: {
    newPassword: "",
    newPasswordRepeat: "",
    oldPassword: "",
  },
  userDataError: {
    emailError: "",
    identificationError: "",
    nameError: "",
    phoneNumberError: "",
  },
  passwordDataError: {
    newPasswordError: "",
    newPasswordRepeatError: "",
    oldPasswordError: "",
  },
  roleOptions: [],
  currentRoles: [],
  currentNumPages: 0,
  currentPage: 1,
  eventCurrentPage: 1,
  eventCurrentNumPages: 0,
  filteredRequest: {
    page: 0,
    sortType: "NAME",
  },
  users: null,
  events: null,
  generatedPassword: null,
  allUsers: [],
  roleProfiles: [],
  roleProfileCreationError: "",
  roleProfileCreationLoading: "idle",
  changeMultipleUsersLoading: "idle",
  changeMultipleUsersError: "",
  extraConfigLoading: "idle",
  extraConfig: null,
  tokenConfig: null,
};

export const getAccountData = createAsyncThunk(
  "configuration/getUserData",
  async () => {
    const userData = await getUserDataAPI();
    return userData;
  }
);

export const restorePassword = createAsyncThunk(
  "configuration/restorePassword",
  async (
    { userId, newPassword }: { userId: string; newPassword?: string },
    { rejectWithValue }
  ) => {
    const passwordData = await restorePasswordAPI(userId, newPassword);
    if (passwordData.result) {
      return passwordData.result;
    } else {
      return rejectWithValue(passwordData.error);
    }
  }
);

export const changeUserRoles = createAsyncThunk(
  "configuration/changeUserRoles",
  async (
    { userId, roleProfileId }: { userId: string; roleProfileId?: string },
    { getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const userData = await changeUserRolesAPI({
      roles: state.configuration.currentRoles,
      userId,
      roleProfileId,
    });
    if (userData.result) {
      return userData.result;
    } else {
      return rejectWithValue(userData.message);
    }
  }
);

export const changeOwnRoles = createAsyncThunk(
  "configuration/changeOwnRoles",
  async (
    { roleProfileId }: { roleProfileId?: string },
    { getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const userData = await changeOwnRolesAPI({
      roles: state.configuration.currentRoles,
      userId: "",
      roleProfileId,
    });
    if (userData.result?.generatedToken) {
      await localforage.setItem("token", userData.result.generatedToken);
    }
    if (userData.result) {
      return userData.result;
    } else {
      return rejectWithValue(userData.message);
    }
  }
);

export const resetOwnPassword = createAsyncThunk(
  "configuration/resetOwnPassword",
  async (_, { getState, rejectWithValue }) => {
    let state = getState() as RootState;
    let oldPassword = state.configuration.passwordData.oldPassword;
    let newPassword = state.configuration.passwordData.newPassword;
    const passwordData = await resetOwnPasswordAPI({
      newPassword,
      oldPassword,
    });
    if (passwordData.result) {
      return passwordData.result;
    } else {
      return rejectWithValue(passwordData.message);
    }
  }
);

export const getOtherUser = createAsyncThunk(
  "configuration/getOtherUser",
  async (id: string) => {
    const userData = await getOtherUserDataAPI(id);
    return userData;
  }
);

export const getRoleOptions = createAsyncThunk(
  "configuration/getRoleOptions",
  async (_, { getState }) => {
    const state = getState() as RootState;
    const activeServices = state.common.activeServices;
    const isMetaClient = state.common.isMetaClient;
    const activeServicesRoles = activeServices.map((x) => {
      switch (x) {
        case "coreId":
          return "coreid";
        case "magicForms":
          return "magicforms";
        case "firmaNew":
          return "firma";
        case "hyperFlow":
          return "hyperflow";
        case "pagares":
          return "pagare";
        case "smartFlow":
          return "smartflow";
        case "authentikator":
          return "authentikator";
        case "dashboard":
          return "dashboard";
        case "manualLists":
          return "lists";
        case "mobileOffice":
          return "mobileoffice";
        case "marcacionesV2":
          return "marcacionesV2";
        case "editor":
          return "editor";
        default:
          return undefined;
      }
    });
    const roleOptions = await getRoleOptionsAPI();
    let roleOptionsResult: PredeterminedRoles[] = [];
    if (roleOptions && roleOptions.length > 0) {
      for (let activeService of activeServicesRoles) {
        if (activeService && activeService !== "smartflow") {
          let role = roleOptions.find((x) =>
            x.adminRole.role.includes(activeService!.toString())
          );

          if (role) {
            roleOptionsResult.push(role);
          }
        }
      }

      let analyticsRoles = roleOptions.find((x) =>
        x.adminRole.role.includes("analytics.admin")
      );
      if (analyticsRoles) {
        let filteredAnalyticsRole: PredeterminedRoles = {
          adminRole: analyticsRoles.adminRole,
          containedRoles: [],
        };
        for (let activeService of activeServicesRoles) {
          if (activeService && activeService !== "smartflow") {
            let role = analyticsRoles.containedRoles.find((x) =>
              x.role.includes(activeService!.toString())
            );
            if (role) {
              filteredAnalyticsRole.containedRoles.push(role);
            }
          }
        }
        roleOptionsResult.push(filteredAnalyticsRole);
      }

      let usersRole = roleOptions.find((x) =>
        x.adminRole.role.includes("users.admin")
      );
      if (usersRole) {
        roleOptionsResult.push(usersRole);
      }

      if (isMetaClient) {
        let clientsRole = roleOptions.find((x) =>
          x.adminRole.role.includes("clients.admin")
        );
        if (clientsRole) {
          roleOptionsResult.push(clientsRole);
        }
      }
      let tagsRole = roleOptions.find((x) =>
        x.adminRole.role.includes("tags.admin")
      );
      if (tagsRole) {
        roleOptionsResult.push(tagsRole);
      }
    }

    return roleOptionsResult;
  }
);

export const getCurrentRoles = createAsyncThunk(
  "configuration/getCurrentRoles",
  async () => {
    const roles = await getCurrentRolesAPI();
    return roles;
  }
);

export const createUser = createAsyncThunk(
  "configuration/createUser",
  async (userData: CreateUserRequest, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    let req: CreateUserRequest = {
      email: userData.email,
      identification: userData.identification,
      name: userData.name,
      password: userData.password,
      phoneNumber: userData.phoneNumber,
      roles: state.configuration.currentRoles,
      roleProfileId: userData.roleProfileId,
      userId: "",
      userName: userData.email,
      setAsesor: userData.setAsesor,
    };
    const result = await createUserAPI(req);
    if (result.error) {
      return rejectWithValue(result.error);
    } else {
      return result.data;
    }
  }
);

export const createMultipleUsers = createAsyncThunk(
  "configuration/createMultipleUsers",
  async (userData: AddMultipleUsersReq, { rejectWithValue }) => {
    const result = await createMultipleUsersAPI(userData);
    if (result.error) {
      return rejectWithValue(result.error);
    } else {
      return result.data;
    }
  }
);

export const changeOwnInfo = createAsyncThunk(
  "configuration/changeOwnInfo",
  async (
    {
      name,
      identification,
      phoneNumber,
      motive,
    }: {
      name: string;
      identification: string;
      phoneNumber: string;
      motive?: string;
    },
    { rejectWithValue }
  ) => {
    let req: ChangeInfoRequest = {
      name: name,
      identification: identification,
      phoneNumber: phoneNumber,
      motive,
    };
    const userData = await changeOwnInfoAPI(req);
    if (userData.userData) {
      return userData.userData;
    } else {
      return rejectWithValue(userData.error);
    }
  }
);

export const changeUserInfo = createAsyncThunk(
  "configuration/changeUserInfo",
  async (
    {
      userId,
      name,
      phoneNumber,
      identification,
      motive,
      setAsesor,
    }: {
      userId: string;
      name: string;
      phoneNumber: string;
      identification: string;
      motive?: string;
      setAsesor?: boolean;
    },
    { rejectWithValue }
  ) => {
    let req: ChangeInfoRequest = {
      identification: identification,
      name: name,
      phoneNumber: phoneNumber,
      motive,
      setAsesor,
    };
    const userData = await changeUserInfoAPI(userId, req);
    if (userData.userData) {
      return userData.userData;
    } else {
      return rejectWithValue(userData.error);
    }
  }
);

export const toggleUserStatus = createAsyncThunk(
  "configuration/toggleUserStatus",
  async (id: string) => {
    const userData = await toggleUserStatusAPI(id);
    return userData;
  }
);

export const toggleMultipleUserStatus = createAsyncThunk(
  "configuration/toggleMultipleUserStatus",
  async (ids: string[], { rejectWithValue }) => {
    const userData = await toggleMultipleUserStatusAPI({ userIds: ids });
    if (userData.error) {
      return rejectWithValue(userData.error);
    } else {
      return userData.result;
    }
  }
);

export const deleteUser = createAsyncThunk(
  "configuration/deleteUser",
  async (id: string, { rejectWithValue }) => {
    const result = await deleteUserAPI(id);
    if (result.userData) {
      return result.userData;
    } else {
      return rejectWithValue(result.error);
    }
  }
);

export const deleteMultipleUser = createAsyncThunk(
  "configuration/deleteMultipleUser",
  async (ids: string[], { rejectWithValue }) => {
    const result = await deleteMultipleUserAPI({ userIds: ids });
    if (result.error) {
      return rejectWithValue(result.error);
    } else {
      return result;
    }
  }
);

export const getFilteredUsers = createAsyncThunk(
  "configuration/getFilteredUsers",
  async (params: FilteredUsersRequest, { rejectWithValue }) => {
    try {
      const filteredUsers = await getFilteredUsersAPI(params);
      if (filteredUsers.filteredUsers) {
        return filteredUsers;
      } else {
        return rejectWithValue("");
      }
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const getFilteredEvents = createAsyncThunk(
  "configuration/getFilteredEvents",
  async (params: GetFilteredEventsRequest, { rejectWithValue }) => {
    try {
      const filteredEvents = await getFilteredEventsAPI(params);
      if (filteredEvents.filteredEvents) {
        return filteredEvents;
      } else {
        return rejectWithValue("");
      }
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const getAllUsers = createAsyncThunk(
  "configuration/getAllUsers",
  async () => {
    const users = await getAllUsersAPI();
    return users;
  }
);

export const getRoleProfiles = createAsyncThunk(
  "configuration/getRoleProfiles",
  async (_, { rejectWithValue }) => {
    const roleProfiles = await getRoleProfilesAPI();
    if (roleProfiles.error) {
      return rejectWithValue(roleProfiles.error);
    } else {
      return roleProfiles.roleProfiles;
    }
  }
);

export const createRoleProfile = createAsyncThunk(
  "configuration/createRoleProfile",
  async (roleProfile: RoleProfile, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const currentRoles = state.configuration.currentRoles;
    roleProfile.roles = currentRoles;
    const newRoleProfile: RoleProfile = {
      ...roleProfile,
      roles: currentRoles,
    };
    const result = await createRoleProfileAPI(newRoleProfile);
    if (result.error) {
      return rejectWithValue(result.error);
    } else {
      return result.roleProfile;
    }
  }
);

export const updateRoleProfile = createAsyncThunk(
  "configuration/updateRoleProfile",
  async (roleProfile: RoleProfile, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const currentRoles = state.configuration.currentRoles;
    const newRoleProfile: RoleProfile = {
      ...roleProfile,
      roles: currentRoles,
    };
    const result = await updateRoleProfileAPI(newRoleProfile);
    if (result.error) {
      return rejectWithValue(result.error);
    } else {
      return result.roleProfile;
    }
  }
);

export const deleteRoleProfile = createAsyncThunk(
  "configuration/deleteRoleProfile",
  async (id: string, { rejectWithValue }) => {
    const result = await deleteRoleProfileAPI(id);
    if (result.error) {
      return rejectWithValue(result.error);
    } else {
      return result.result;
    }
  }
);

export const getExtraConfig = createAsyncThunk(
  "configuration/getExtraConfig",
  async (_, { rejectWithValue }) => {
    const extraConfig = await getExtraConfigAPI();
    if (extraConfig.extraConfig) {
      return extraConfig.extraConfig;
    } else {
      return rejectWithValue(extraConfig.error);
    }
  }
);

export const updateExtraConfig = createAsyncThunk(
  "configuration/updateExtraConfig",
  async (extraConfig: ClientExtraConfig, { rejectWithValue }) => {
    const result = await changeExtraConfigAPI(extraConfig);
    if (result.extraConfig) {
      return result.extraConfig;
    } else {
      return rejectWithValue(result.error);
    }
  }
);

export const getAuthConfig = createAsyncThunk(
  "configuration/getAuthConfig",
  async (_, { rejectWithValue }) => {
    const authConfig = await getAuthConfigAPI();
    if (authConfig.authConfig) {
      return authConfig.authConfig;
    } else {
      return rejectWithValue(authConfig.error);
    }
  }
);

export const updateAuthConfig = createAsyncThunk(
  "configuration/updateAuthConfig",
  async (authConfig: ClientTokenConfig, { rejectWithValue }) => {
    await updateAuthConfigAPI(authConfig);
    const result = await getAuthConfigAPI();
    if (result.authConfig) {
      return result.authConfig;
    } else {
      return rejectWithValue(result.error);
    }
  }
);

export const configurationSlice = createSlice({
  name: "configurationSlice",
  initialState,
  reducers: {
    changeConfigurationActiveComponent: (
      state,
      action: PayloadAction<ConfigurationActiveComponent>
    ) => {
      state.activeComponent = action.payload;
    },
    changeHelpActiveComponent: (
      state,
      action: PayloadAction<HelpActiveComponent>
    ) => {
      state.helpActiveComponent = action.payload;
    },
    cleanCurrentRoles: (state) => {
      state.currentRoles = [];
      state.currentRolesLoading = "idle";
    },
    addRolesToCurrent: (state, action: PayloadAction<string[]>) => {
      let currentRolesTemp: string[] = [];
      action.payload.forEach((role) => {
        if (!state.currentRoles.includes(role)) {
          currentRolesTemp.push(role);
        }
      });

      state.currentRoles = [...state.currentRoles, ...currentRolesTemp];
    },
    removeRolesFromCurrent: (state, action: PayloadAction<string[]>) => {
      let currentRolesfiltered: string[] = [...state.currentRoles];
      action.payload.forEach((d) => {
        currentRolesfiltered = currentRolesfiltered.filter((r) => r !== d);
      });

      state.currentRoles = [...currentRolesfiltered];
    },
    setCurrentRoles: (state, action: PayloadAction<string[]>) => {
      state.currentRoles = action.payload;
    },
    changePageState: (state, action: PayloadAction<number>) => {
      let current = state.filteredRequest;
      state.filteredRequest = { ...current, page: action.payload };
    },
    changeEventPageState: (state, action: PayloadAction<number>) => {
      let current = state.filteredEventRequest;
      state.filteredEventRequest = { ...current, page: action.payload };
    },
    changeEventPageSize: (state, action: PayloadAction<number>) => {
      let current = state.filteredEventRequest;
      state.filteredEventRequest = { ...current, pageSize: action.payload };
    },
    changeActiveComponent: (
      state,
      action: PayloadAction<ConfigurationActiveComponent>
    ) => {
      state.activeComponent = action.payload;
      state.currentPage = 1;
      state.currentNumPages = 0;
    },
    changeCurrentFilters: (
      state,
      action: PayloadAction<FilteredUsersRequest>
    ) => {
      state.filteredRequest = { ...action.payload, page: 0 };
    },
    changeCurrentEventFilters: (
      state,
      action: PayloadAction<GetFilteredEventsRequest>
    ) => {
      state.filteredEventRequest = { ...action.payload, page: 0 };
    },
    emptyGeneratedPassword: (state) => {
      state.generatedPassword = null;
      state.restorePasswordLoading = "idle";
      state.createUserLoading = "idle";
      state.createUserError = undefined;
      state.createMultipleUsersResult = undefined;
    },
    changePasswordData: (state, action: PayloadAction<PasswordData>) => {
      state.passwordData = action.payload;
    },
    changePasswordErrorData: (
      state,
      action: PayloadAction<PasswordDataError>
    ) => {
      state.passwordDataError = action.payload;
    },
    changeLoadingResetPassword: (
      state,
      action: PayloadAction<LoadingStatus>
    ) => {
      state.resetOwnPasswordLoading = action.payload;
    },
    changeLoadingEditOwnInfo: (state, action: PayloadAction<LoadingStatus>) => {
      state.changeOwnInfoLoading = action.payload;
    },
    changeCurrentData: (state, action: PayloadAction<UserData | null>) => {
      state.currentUserData = action.payload;
    },
    changeCurrentDataError: (
      state,
      action: PayloadAction<UserDataErrorMessage>
    ) => {
      state.userDataError = action.payload;
    },

    changeAccountLoading: (state, action: PayloadAction<LoadingStatus>) => {
      state.accountLoadingStatus = action.payload;
    },
    resetOtherUserData: (state) => {
      state.otherUser = null;
      state.otherUserLoading = "idle";
      state.currentRoles = [];
      state.generatedPassword = "";
      state.restorePasswordLoading = "idle";
      state.changeUserInfoLoading = "idle";
      state.deleteUserLoading = "idle";
      state.changeUserInfoError = undefined;
      state.changeRolesError = undefined;
    },
    resetRoleProfiledata: (state) => {
      state.currentRoles = [];
      state.roleProfileCreationError = "";
      state.roleProfileCreationLoading = "idle";
    },
    resetChangeUserData: (state) => {
      state.changeMultipleUsersError = "";
      state.changeMultipleUsersLoading = "idle";
    },
    setChangeUserInfoError(state, action: PayloadAction<string | undefined>) {
      state.changeUserInfoError = action.payload;
    },
    setChangeRolesError(state, action: PayloadAction<string | undefined>) {
      state.changeRolesError = action.payload;
    },
    changeExtraConfig: (state, action: PayloadAction<ClientExtraConfig>) => {
      state.extraConfig = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccountData.pending, (state) => {
        state.accountLoadingStatus = "pending";
      })
      .addCase(getAccountData.fulfilled, (state, action) => {
        state.currentUserData = action.payload;
        if (action.payload) {
          state.accountLoadingStatus = "resolved";
        } else {
          state.accountLoadingStatus = "rejected";
        }
      })
      .addCase(getRoleOptions.pending, (state) => {
        state.roleOptionsLoading = "pending";
      })
      .addCase(getRoleOptions.fulfilled, (state, action) => {
        state.roleOptions = action.payload;
        if (action.payload.length > 0) {
          state.roleOptionsLoading = "resolved";
        } else {
          state.roleOptionsLoading = "rejected";
        }
      })
      .addCase(getCurrentRoles.pending, (state) => {
        state.currentRolesLoading = "pending";
      })
      .addCase(getCurrentRoles.fulfilled, (state, action) => {
        state.currentRoles = action.payload;
        if (action.payload.length > 0) {
          state.currentRolesLoading = "resolved";
        } else {
          state.currentRolesLoading = "rejected";
        }
      })
      .addCase(getFilteredUsers.fulfilled, (state, action) => {
        let users = action.payload;

        if (users.filteredUsers) {
          state.users = users.filteredUsers;
          state.currentPage = users.filteredUsers.currentPage;
          state.currentNumPages = users.filteredUsers.numPages;
          state.usersLoading = "resolved";
        } else {
          state.usersLoading = "rejected";
        }
      })
      .addCase(getFilteredUsers.rejected, (state) => {
        state.usersLoading = "rejected";
        state.currentPage = 0;
        state.currentNumPages = 0;
      })
      .addCase(getFilteredUsers.pending, (state) => {
        state.usersLoading = "pending";
      });

    // User creation
    builder
      .addCase(createUser.rejected, (state, action) => {
        state.generatedPassword = null;
        state.createUserError = action.payload as string;
        state.createUserLoading = "rejected";
      })
      .addCase(createUser.fulfilled, (state, action) => {
        if (action.payload) {
          state.generatedPassword = action.payload.generatedPassword;
        }
        state.createUserLoading = "resolved";
      })
      .addCase(createUser.pending, (state) => {
        state.createUserLoading = "pending";
      });

    builder
      .addCase(createMultipleUsers.fulfilled, (state, action) => {
        if (action.payload) {
          state.createMultipleUsersResult = action.payload;
        }
        state.createUserLoading = "resolved";
        state.createUserError = undefined;
      })
      .addCase(createMultipleUsers.rejected, (state, action) => {
        state.createUserError = action.payload as string;
        state.createUserLoading = "rejected";
      })
      .addCase(createMultipleUsers.pending, (state) => {
        state.createUserLoading = "pending";
      });

    builder
      .addCase(getOtherUser.pending, (state) => {
        state.otherUserLoading = "pending";
      })
      .addCase(getOtherUser.fulfilled, (state, action) => {
        state.otherUser = action.payload;
        if (action.payload) {
          state.otherUserLoading = "resolved";
          state.currentRoles = action.payload.roles;
        } else {
          state.otherUserLoading = "rejected";
        }
      });
    // Restore password
    builder
      .addCase(restorePassword.pending, (state) => {
        state.restorePasswordError = undefined;
        state.restorePasswordLoading = "pending";
      })
      .addCase(restorePassword.fulfilled, (state, action) => {
        state.restorePasswordError = undefined;
        state.restorePasswordLoading = "resolved";
        state.generatedPassword = action.payload.generatedPassword;
      })
      .addCase(restorePassword.rejected, (state, action) => {
        state.restorePasswordError = action.payload as string;
        state.restorePasswordLoading = "rejected";
        state.generatedPassword = null;
      })
      .addCase(resetOwnPassword.pending, (state) => {
        state.resetOwnPasswordLoading = "pending";
      })
      .addCase(resetOwnPassword.fulfilled, (state) => {
        state.resetOwnPasswordLoading = "resolved";
      })
      .addCase(resetOwnPassword.rejected, (state, action) => {
        state.resetOwnPasswordLoading = "rejected";
        state.passwordDataError.oldPasswordError = action.payload as string;
      })
      .addCase(changeOwnInfo.pending, (state) => {
        state.changeUserInfoError = undefined;
        state.changeOwnInfoLoading = "pending";
      })
      .addCase(changeOwnInfo.fulfilled, (state, action) => {
        state.changeUserInfoError = undefined;
        state.changeOwnInfoLoading = "resolved";
        state.currentUserData = action.payload;
        state.changeOwnInfoLoading = "rejected";
      })
      .addCase(changeOwnInfo.rejected, (state, action) => {
        state.changeOwnInfoLoading = "rejected";
        state.changeUserInfoError = action.payload as string;
      })
      .addCase(changeUserInfo.pending, (state) => {
        state.changeUserInfoError = undefined;
        state.changeUserInfoLoading = "pending";
      })
      .addCase(changeUserInfo.fulfilled, (state, action) => {
        state.changeUserInfoError = undefined;
        state.changeUserInfoLoading = "resolved";
        state.otherUser = action.payload;
      })
      .addCase(changeUserInfo.rejected, (state, action) => {
        state.changeUserInfoLoading = "rejected";
        state.changeUserInfoError = action.payload as string;
      });
    // Change user status
    builder
      .addCase(toggleUserStatus.pending, (state) => {
        state.changeUserInfoLoading = "pending";
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        if (action.payload.userData) {
          state.changeUserInfoLoading = "resolved";
          state.otherUser = action.payload.userData;
        } else {
          state.changeUserInfoLoading = "rejected";
        }
      });

    builder
      .addCase(toggleMultipleUserStatus.fulfilled, (state) => {
        state.changeMultipleUsersError = "";
        state.changeMultipleUsersLoading = "resolved";
      })
      .addCase(toggleMultipleUserStatus.pending, (state) => {
        state.changeMultipleUsersError = "";
        state.changeMultipleUsersLoading = "pending";
      })
      .addCase(toggleMultipleUserStatus.rejected, (state, action) => {
        state.changeMultipleUsersError = action.payload as string;
        state.changeMultipleUsersLoading = "rejected";
      });

    // Delete users
    builder
      .addCase(deleteUser.pending, (state) => {
        state.deleteUserError = undefined;
        state.deleteUserLoading = "pending";
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.deleteUserError = undefined;
        state.deleteUserLoading = "resolved";
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleteUserError = action.payload as string;
        state.deleteUserLoading = "rejected";
      });

    builder
      .addCase(deleteMultipleUser.fulfilled, (state) => {
        state.changeMultipleUsersLoading = "resolved";
      })
      .addCase(deleteMultipleUser.pending, (state) => {
        state.changeMultipleUsersLoading = "pending";
      })
      .addCase(deleteMultipleUser.rejected, (state, action) => {
        state.changeMultipleUsersError = action.payload as string;
        state.changeMultipleUsersLoading = "rejected";
      });

    // Change user roles
    builder
      .addCase(changeUserRoles.pending, (state) => {
        state.changeRolesError = undefined;
        state.changeRolesLoading = "pending";
      })
      .addCase(changeUserRoles.fulfilled, (state, action) => {
        state.changeRolesError = undefined;
        state.changeRolesLoading = "resolved";
        state.otherUser = action.payload;
      })
      .addCase(changeUserRoles.rejected, (state, action) => {
        state.changeRolesError = action.payload as string;
        state.changeRolesLoading = "rejected";
      })
      .addCase(changeOwnRoles.pending, (state) => {
        state.changeRolesError = undefined;
        state.changeRolesLoading = "pending";
      })
      .addCase(changeOwnRoles.fulfilled, (state, action) => {
        state.changeRolesError = undefined;
        state.changeRolesLoading = "resolved";
        state.currentUserData = action.payload;
        state.changeRolesLoading = "rejected";
      })
      .addCase(changeOwnRoles.rejected, (state, action) => {
        state.changeRolesError = action.payload as string;
        state.changeRolesLoading = "rejected";
      });

    // Get filtered events
    builder
      .addCase(getFilteredEvents.pending, (state) => {
        state.eventsLoading = "pending";
      })
      .addCase(getFilteredEvents.fulfilled, (state, action) => {
        let events = action.payload;

        if (events.filteredEvents) {
          state.events = events.filteredEvents;
          state.eventCurrentPage = events.filteredEvents.currentPage;
          state.eventCurrentNumPages = events.filteredEvents.numPages;
          state.eventsLoading = "resolved";
        } else {
          state.eventsLoading = "rejected";
        }
      })
      .addCase(getFilteredEvents.rejected, (state) => {
        state.eventsLoading = "rejected";
        state.eventCurrentPage = 0;
        state.eventCurrentNumPages = 0;
      });

    // all users
    builder.addCase(getAllUsers.fulfilled, (state, action) => {
      let users = action.payload;

      if (users) {
        state.allUsers = users;
        state.usersLoading = "resolved";
      } else {
        state.usersLoading = "rejected";
      }
    });
    //Get Extra Config
    builder
      .addCase(getExtraConfig.pending, (state) => {
        state.extraConfigLoading = "pending";
      })
      .addCase(getExtraConfig.fulfilled, (state, action) => {
        if (action.payload) {
          state.extraConfig = action.payload;
          state.extraConfigLoading = "resolved";
        } else {
          state.extraConfigLoading = "rejected";
        }
      })
      .addCase(getExtraConfig.rejected, (state) => {
        state.extraConfigLoading = "rejected";
      });

    //Update Extra Config
    builder
      .addCase(updateExtraConfig.pending, (state) => {
        state.extraConfigLoading = "pending";
      })
      .addCase(updateExtraConfig.fulfilled, (state, action) => {
        if (action.payload) {
          state.extraConfig = action.payload;
          state.extraConfigLoading = "resolved";
        } else {
          state.extraConfigLoading = "rejected";
        }
      })
      .addCase(updateExtraConfig.rejected, (state) => {
        state.extraConfigLoading = "rejected";
      });

    // getting role profiles
    builder.addCase(getRoleProfiles.fulfilled, (state, action) => {
      let roleProfiles = action.payload;

      if (roleProfiles) {
        state.roleProfiles = roleProfiles;
      }
    });

    // create role profile
    builder
      .addCase(createRoleProfile.fulfilled, (state) => {
        state.roleProfileCreationLoading = "resolved";
      })
      .addCase(createRoleProfile.rejected, (state, action) => {
        state.roleProfileCreationError = action.payload as string;
        state.roleProfileCreationLoading = "rejected";
      })
      .addCase(createRoleProfile.pending, (state) => {
        state.roleProfileCreationLoading = "pending";
      });

    // update role profile
    builder
      .addCase(updateRoleProfile.fulfilled, (state) => {
        state.roleProfileCreationLoading = "resolved";
      })
      .addCase(updateRoleProfile.rejected, (state, action) => {
        state.roleProfileCreationError = action.payload as string;
        state.roleProfileCreationLoading = "rejected";
      })
      .addCase(updateRoleProfile.pending, (state) => {
        state.roleProfileCreationLoading = "pending";
      });
    // delete role profile
    builder
      .addCase(deleteRoleProfile.fulfilled, (state) => {
        state.roleProfileCreationLoading = "resolved";
      })
      .addCase(deleteRoleProfile.rejected, (state, action) => {
        state.roleProfileCreationError = action.payload as string;
        state.roleProfileCreationLoading = "rejected";
      })
      .addCase(deleteRoleProfile.pending, (state) => {
        state.roleProfileCreationLoading = "pending";
      });

    // get token config
    builder
      .addCase(getAuthConfig.fulfilled, (state, action) => {
        state.tokenConfig = action.payload;
        state.tokenConfigLoading = "resolved";
      })
      .addCase(getAuthConfig.rejected, (state) => {
        state.tokenConfigLoading = "rejected";
        state.tokenConfig = null;
      })
      .addCase(getAuthConfig.pending, (state) => {
        state.tokenConfigLoading = "pending";
      });

    builder
      .addCase(updateAuthConfig.fulfilled, (state, action) => {
        state.tokenConfig = action.payload;
        state.tokenConfigLoading = "resolved";
      })
      .addCase(updateAuthConfig.rejected, (state) => {
        state.tokenConfigLoading = "rejected";
        state.tokenConfig = null;
      })
      .addCase(updateAuthConfig.pending, (state) => {
        state.tokenConfigLoading = "pending";
      });
  },
});

export const {
  changeConfigurationActiveComponent,
  cleanCurrentRoles,
  addRolesToCurrent,
  removeRolesFromCurrent,
  changePageState,
  changeActiveComponent,
  changeCurrentFilters,
  emptyGeneratedPassword,
  changePasswordData,
  changePasswordErrorData,
  changeLoadingResetPassword,
  changeCurrentData,
  changeCurrentDataError,
  changeLoadingEditOwnInfo,
  changeAccountLoading,
  resetOtherUserData,
  changeHelpActiveComponent,
  setCurrentRoles,
  changeCurrentEventFilters,
  changeEventPageState,
  changeEventPageSize,
  resetRoleProfiledata,
  resetChangeUserData,
  setChangeRolesError,
  setChangeUserInfoError,
  changeExtraConfig,
} = configurationSlice.actions;

export const selectActiveConfigurationComponent = (state: RootState) =>
  state.configuration.activeComponent;
export const selectLoadingAccount = (state: RootState) =>
  state.configuration.accountLoadingStatus;
export const selectAccountData = (state: RootState) =>
  state.configuration.currentUserData;
export const selectAccountDataError = (state: RootState) =>
  state.configuration.userDataError;
export const selectPasswordChangeLoading = (state: RootState) =>
  state.configuration.passwordChangeStatus;
export const selectPasswordData = (state: RootState) =>
  state.configuration.passwordData;
export const selectPasswordDataError = (state: RootState) =>
  state.configuration.passwordDataError;
export const selectRoleOptions = (state: RootState) =>
  state.configuration.roleOptions;
export const selectRoleOptionsLoading = (state: RootState) =>
  state.configuration.roleOptionsLoading;
export const selectCurrentRoles = (state: RootState) =>
  state.configuration.currentRoles;
export const selectCurrentRolesLoading = (state: RootState) =>
  state.configuration.currentRolesLoading;
export const selectUsersLoading = (state: RootState) =>
  state.configuration.usersLoading;
export const selectUsers = (state: RootState) => state.configuration.users;
export const selectCurrentPageUsers = (state: RootState) =>
  state.configuration.currentPage;
export const selectCurrentNumPagesUsers = (state: RootState) =>
  state.configuration.currentNumPages;
export const selectCurrentFilterRequestUsers = (state: RootState) =>
  state.configuration.filteredRequest;
export const selectCreateUserLoading = (state: RootState) =>
  state.configuration.createUserLoading;
export const selectLoadingOtherUser = (state: RootState) =>
  state.configuration.otherUserLoading;
export const selectOtherUser = (state: RootState) =>
  state.configuration.otherUser;
export const selectGeneratedPassword = (state: RootState) =>
  state.configuration.generatedPassword;
export const selectRestorePasswordLoading = (state: RootState) =>
  state.configuration.restorePasswordLoading;
export const selectResetOwnPasswordLoading = (state: RootState) =>
  state.configuration.resetOwnPasswordLoading;
export const selectChangeOwnInfoLoading = (state: RootState) =>
  state.configuration.changeOwnInfoLoading;
export const selectChangeUserInfoLoading = (state: RootState) =>
  state.configuration.changeUserInfoLoading;
export const selectDeleteLoading = (state: RootState) =>
  state.configuration.deleteUserLoading;
export const selectChangeRolesLoading = (state: RootState) =>
  state.configuration.changeRolesLoading;
export const selectHelpActiveComponent = (state: RootState) =>
  state.configuration.helpActiveComponent;
export const selectCurrentEvents = (state: RootState) =>
  state.configuration.events;
export const selectCurrentEventFilters = (state: RootState) =>
  state.configuration.filteredEventRequest;
export const selectLoadingEvents = (state: RootState) =>
  state.configuration.eventsLoading;
export const selectEventsCurrentPage = (state: RootState) =>
  state.configuration.eventCurrentPage;
export const selectEventsCurrentNumPages = (state: RootState) =>
  state.configuration.eventCurrentNumPages;
export const selectEventsPageSize = (state: RootState) =>
  state.configuration.filteredEventRequest.pageSize;
export const selectAllUsers = (state: RootState) =>
  state.configuration.allUsers;
export const selectRoleProfiles = (state: RootState) =>
  state.configuration.roleProfiles;
export const selectRPCreationLoading = (state: RootState) =>
  state.configuration.roleProfileCreationLoading;
export const selectRPCreationError = (state: RootState) =>
  state.configuration.roleProfileCreationError;
export const selectCreateUserError = (state: RootState) =>
  state.configuration.createUserError;
export const selectCreateMultipleUsersResult = (state: RootState) =>
  state.configuration.createMultipleUsersResult;
export const selectChangeMultipleUsersError = (state: RootState) =>
  state.configuration.changeMultipleUsersError;
export const selectChangeMultipleUsersLoading = (state: RootState) =>
  state.configuration.changeMultipleUsersLoading;
export const selectChangeRolesError = (state: RootState) =>
  state.configuration.changeRolesError;
export const selectChangeUserInfoError = (state: RootState) =>
  state.configuration.changeUserInfoError;
export const selectDeleteUserError = (state: RootState) =>
  state.configuration.deleteUserError;
export const selectRestorePasswordError = (state: RootState) =>
  state.configuration.restorePasswordError;
export const selectExtraDataLoading = (state: RootState) =>
  state.configuration.extraConfigLoading;
export const selectExtraConfig = (state: RootState) =>
  state.configuration.extraConfig;
export const selectTokenConfig = (state: RootState) =>
  state.configuration.tokenConfig;
export const selectTokenConfigLoading = (state: RootState) =>
  state.configuration.tokenConfigLoading;

export default configurationSlice.reducer;
